import { BaselineSection } from '../BaselineSection';
import { type StepForm, WizardField } from '../WizardField';

interface Props {
  form: StepForm;
}

const BASELINE_FIELDS = [
  {
    name: 'step5.baselineAssessedRealProperty',
    label: 'Existing assessed real property',
    format: 'currency' as const,
  },
  {
    name: 'step5.baselineAssessedBpp',
    label: 'Existing assessed business personal property',
    format: 'currency' as const,
  },
  {
    name: 'step5.baselineAssessedInventory',
    label: 'Existing assessed inventory',
    format: 'currency' as const,
  },
];

export function Step5Property({ form }: Props) {
  const projectType = form.watch('step1.projectType');

  // Offer a non-destructive estimate of Year-1 assessed values from the
  // capital costs already entered in Step 2, for directors who don't yet have
  // the county appraisal. Real property ~ land + buildings; BPP ~ machinery +
  // FF&E; inventory ~ inventory.
  const cap = form
    .watch([
      'step2.land',
      'step2.buildings',
      'step2.machineryEquipment',
      'step2.ffe',
      'step2.inventory',
    ])
    .map((v) => Number(v) || 0);
  const [land, buildings, machinery, ffe, inventory] = cap;
  const suggestedReal = (land ?? 0) + (buildings ?? 0);
  const suggestedBpp = (machinery ?? 0) + (ffe ?? 0);
  const suggestedInventory = inventory ?? 0;
  const hasCapital = suggestedReal + suggestedBpp + suggestedInventory > 0;

  const applyEstimate = () => {
    form.setValue('step5.assessedRealProperty', suggestedReal, { shouldValidate: true });
    form.setValue('step5.assessedBpp', suggestedBpp, { shouldValidate: true });
    form.setValue('step5.assessedInventory', suggestedInventory, { shouldValidate: true });
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-slate-800">Property & Assessment</h3>
        <p className="mt-0.5 text-sm text-slate-500">
          Year-1 assessed values. Appreciation and depreciation rates are set by your EDC
          methodology profile.
        </p>
      </div>

      {hasCapital && (
        <div className="flex items-start justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <p className="text-slate-600">
            Don&rsquo;t have the county appraisal yet? Estimate Year-1 assessed values from the
            capital costs you entered in Step 2. These are rough estimates, so adjust them if you
            have the assessment.
          </p>
          <button
            type="button"
            onClick={applyEstimate}
            className="shrink-0 whitespace-nowrap rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Estimate from capital
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <WizardField
          form={form}
          name="step5.assessedRealProperty"
          label="Assessed real property — Year 1"
          format="currency"
          hint="Land + buildings on appraisal roll"
        />

        <WizardField
          form={form}
          name="step5.assessedBpp"
          label="Assessed business personal property — Year 1"
          format="currency"
          hint="Equipment, machinery, furniture"
        />

        <WizardField
          form={form}
          name="step5.assessedInventory"
          label="Assessed inventory — Year 1"
          format="currency"
        />
      </div>

      <BaselineSection form={form} projectType={projectType} fields={BASELINE_FIELDS} />
    </div>
  );
}
